import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { getPlatform, type AppInfo } from '@/lib/platform';
import { useAppUpdateRequired } from '../model/appUpdateRequired';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.volleystatspro.app';

/**
 * Covers the whole app once the API refuses this build (426, VOL-14). Unsent
 * entries stay in the app's storage, which an update keeps, and leave after it.
 */
export function AppUpdateRequired() {
  const required = useAppUpdateRequired((s) => s.required);
  const { t } = useTranslation('common');
  const [info, setInfo] = useState<AppInfo | null>(null);

  useEffect(() => {
    if (!required) return;
    getPlatform()
      .app.info()
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [required]);

  if (!required) return null;

  // No App Store listing yet: on iOS a sentence sends the user to the store instead of a button.
  const storeUrl = info?.os === 'android' ? PLAY_STORE_URL : null;
  const openStore = () => {
    if (!storeUrl) return;
    getPlatform()
      .browser.open(storeUrl)
      .catch(() => toast.error(t('nativeApp.open_error')));
  };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="app-update-title"
      aria-describedby="app-update-body"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-50 px-4 pt-[var(--safe-area-inset-top,0px)] pb-[var(--safe-area-inset-bottom,0px)]"
    >
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
          <Download size={22} className="text-slate-500" aria-hidden="true" />
        </div>
        <h1 id="app-update-title" className="mb-3 font-display text-2xl font-bold tracking-tight text-brand-900">
          {t('nativeApp.update.title')}
        </h1>
        <p id="app-update-body" className="mb-3 text-base text-slate-600">
          {t('nativeApp.update.body')}
        </p>
        <p className="mb-6 text-sm text-slate-500">{t('nativeApp.update.entries_kept')}</p>
        {!storeUrl && info && (
          <p className="text-base font-medium text-brand-900">{t('nativeApp.update.open_store')}</p>
        )}
        {storeUrl && (
          <button
            type="button"
            autoFocus
            onClick={openStore}
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-action px-4 py-2 font-display text-base font-bold text-white shadow-sm transition-colors hover:bg-action/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2"
          >
            <Download size={18} aria-hidden="true" />
            {t('nativeApp.update.action')}
          </button>
        )}
      </div>
    </div>
  );
}
